import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { FiEdit2, FiTrash2, FiPlus, FiCheck, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      const response = await axios.post('/api/categories', { name: newCategory });
      setCategories([...categories, response.data]);
      setNewCategory('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add category');
    }
  };

  const handleUpdateCategory = async (id, newName) => {
    try {
      await axios.patch(`/api/categories/${id}`, { name: newName });
      setCategories(
        categories.map((cat) =>
          cat.id === id ? { ...cat, name: newName } : cat
        )
      );
      setEditingCategory(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure? This will affect all portfolio items in this category.')) return;

    try {
      await axios.delete(`/api/categories/${id}`);
      setCategories(categories.filter((cat) => cat.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchCategories}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Portfolio Categories</h1>
      
      <form onSubmit={handleAddCategory} className="mb-8">
        <div className="flex gap-2">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category name"
            className="flex-1 p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center transition-colors"
          >
            <FiPlus className="mr-2" />
            Add Category
          </button>
        </div>
      </form>

      <div className="space-y-4">
        {categories.map((category) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-between p-4 bg-white rounded-lg shadow"
          >
            {editingCategory === category.id ? (
              <input
                type="text"
                defaultValue={category.name}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUpdateCategory(category.id, e.target.value);
                  } else if (e.key === 'Escape') {
                    setEditingCategory(null);
                  }
                }}
                className="flex-1 p-2 border rounded mr-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            ) : (
              <span className="text-lg">{category.name}</span>
            )}
            
            <div className="flex items-center gap-2">
              {editingCategory === category.id ? (
                <>
                  <button
                    onClick={(e) => handleUpdateCategory(category.id, e.target.previousSibling.value)}
                    className="text-green-500 hover:text-green-600 p-2 rounded-full hover:bg-green-50 transition-colors"
                  >
                    <FiCheck size={20} />
                  </button>
                  <button
                    onClick={() => setEditingCategory(null)}
                    className="text-gray-500 hover:text-gray-600 p-2 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <FiX size={20} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditingCategory(category.id)}
                    className="text-blue-500 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    <FiEdit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-500 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        ))}

        {categories.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No categories found</p>
            <p className="text-sm text-gray-400 mt-2">Add your first category using the form above</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryList;
